import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BenefitsPageResponseDto,
  OrgInformationalBenefit,
  useBenefitsQuery,
} from 'theSource/queries';
import { Spacer } from 'theSource/components';
import { Card } from 'components';
import {
  BenefitChipData,
  ContributionBenefitTile,
} from './components/ContributionBenefitTile';
import { HomePageSection } from '../HomePageSection';
import { HomePageCarousel } from '../HomePageSection/HomePageCarousel';
import { EmployerSingleSponsoredTile } from './components/EmployerSponsoredTile/EmployerSingleSponsoredTile';

export function AccountsOtherBenefits() {
  const { t } = useTranslation('pages.home');
  const [fsaCoverages, setFsaCoverages] = useState<BenefitChipData[]>([]);
  const [hsaCoverages, setHsaCoverages] = useState<BenefitChipData[]>([]);
  const [orgInformationalBenefits, setOrgInformationalBenefits] = useState<
    OrgInformationalBenefit[]
  >([]);
  const { data } = useBenefitsQuery({ enabled: true });

  const showContributionBenefitTile =
    fsaCoverages.length !== 0 || hsaCoverages.length !== 0;

  const showEmployerSponsoredTile = orgInformationalBenefits.length !== 0;
  const multipleSponsoredBenefits = orgInformationalBenefits.length > 1;

  function parseBenefitsData({
    data,
    type,
  }: {
    data: BenefitsPageResponseDto;
    type: 'hsa' | 'fsa';
  }): BenefitChipData[] {
    const currentData = data.data.current;
    const benefitLookupCode = type === 'hsa' ? 'hsa' : 'premium_fsa';
    const isFsa = type === 'fsa';

    return currentData
      .filter(
        (coverage) =>
          coverage.benefit_lookup_code === benefitLookupCode &&
          coverage.coverage.req_coverage_level_lookup_code !== 'decline'
      )
      .map((coverage) => {
        const details = coverage.coverage;
        const totalAmount =
          Number(details.req_subscriber_fsa_amount) +
          Number(details.req_org_fsa_amount);

        return {
          name: details.org_plan_name,
          amount: totalAmount,
          carrierName: details.carrier_name,
          carrierImageSrc: details.carrier_logo_url,
          useByDate: isFsa ? details.plan_year_end_date : undefined,
        };
      });
  }

  useEffect(() => {
    if (data) {
      const fsaCoverages = parseBenefitsData({ data, type: 'fsa' });
      const hsaCoverages = parseBenefitsData({ data, type: 'hsa' });
      setFsaCoverages(fsaCoverages);
      setHsaCoverages(hsaCoverages);
      setOrgInformationalBenefits(data.data.org_informational_benefits);
    }
  }, [data]);

  if (!showContributionBenefitTile && !showEmployerSponsoredTile) {
    return null;
  }

  return (
    <>
      {showContributionBenefitTile && (
        <HomePageSection
          title={t('accountsOtherBenefits.titleMyAccounts')}
          additionalClasses="order-md-4 order-3"
          useGap={true}
        >
          <Card
            dataTestId="contribution-benefit-tile-card"
            additionalClasses="col-12 col-lg"
          >
            {hsaCoverages.length !== 0 && (
              <ContributionBenefitTile
                title={t('accountsOtherBenefits.plannedForFuture')}
                benefits={hsaCoverages}
                greenCard
              />
            )}

            {hsaCoverages.length !== 0 && fsaCoverages.length !== 0 && (
              <Spacer height={4} />
            )}

            {fsaCoverages.length !== 0 && (
              <ContributionBenefitTile
                title={t('accountsOtherBenefits.plannedForYear')}
                benefits={fsaCoverages}
              />
            )}
          </Card>
        </HomePageSection>
      )}

      {showEmployerSponsoredTile && (
        <HomePageSection
          title={t('accountsOtherBenefits.titleOtherBenefits')}
          additionalClasses="order-md-5 order-4"
          useGap={true}
        >
          <div
            data-testid="employer-sponsor-tile-card"
            id="employer-sponsored-tile-card"
          >
            {multipleSponsoredBenefits ? (
              <HomePageCarousel>
                {orgInformationalBenefits.map((benefit) => (
                  <EmployerSingleSponsoredTile
                    key={benefit.id}
                    benefit={benefit}
                    isCenter={false}
                  />
                ))}
              </HomePageCarousel>
            ) : (
              <EmployerSingleSponsoredTile
                benefit={orgInformationalBenefits[0]}
                isCenter={false}
              />
            )}
          </div>
        </HomePageSection>
      )}
    </>
  );
}
